import { PrescriptionPadStep } from '@/components/PatientPageV2/components/PrescriptionPad';
import { usePrescriptionAssistantTracking } from '@/components/PatientPageV2/hooks/usePrescriptionAssistantTracking';
import { useAppStore } from '@/state-management';
import { useMemo } from 'react';
import { useDispenseLimitExceededActions } from '../../hooks/useDispenseLimitExceededActions';
import { getPrimaryButtonTooltip } from './PrescriptionAssistantTool.utils';

export const usePrescriptionAssistantActions = ({
  onCreateScript,
  isLoading,
}: {
  onCreateScript: () => Promise<void>;
  isLoading?: boolean;
}) => {
  const {
    prescriptionPad: { step: createScriptStep, actions: reviewActions },
    prescriptionHistory: { itemsToAction },
  } = useAppStore.use.prescriptionAssistant();
  const isPrescriptionPadInSync = useAppStore.use.isPrescriptionPadInSync()();
  const isValidPrescriptionPad = useAppStore.use.isValidPrescriptionPad()();
  const advancePrescriptionPadStep = useAppStore.use.advancePrescriptionPadStep();

  const { trackReviewScript } = usePrescriptionAssistantTracking();

  const isActionChecklistIncomplete = useMemo(
    () => createScriptStep === PrescriptionPadStep.Confirm && reviewActions.some((action) => !action.checked),
    [createScriptStep, reviewActions],
  );
  const hasCompletedActions = itemsToAction.length === 0;
  const { shouldDisable: shouldDisableDueToDispenseLimitExceeded } = useDispenseLimitExceededActions();
  const primaryButtonTooltip = getPrimaryButtonTooltip({
    isActionChecklistIncomplete,
    isPrescriptionPadInSync,
    hasCompletedActions,
    isDispenseLimitExceededNoteEmpty: shouldDisableDueToDispenseLimitExceeded,
  });

  const isPrimaryButtonDisabled =
    isLoading ||
    !isValidPrescriptionPad ||
    !hasCompletedActions ||
    isActionChecklistIncomplete ||
    !isPrescriptionPadInSync ||
    shouldDisableDueToDispenseLimitExceeded;

  const handleReviewFlow = async () => {
    if (createScriptStep === PrescriptionPadStep.Create) {
      // Track Review Script event only when we're on the Create step
      trackReviewScript();
    }

    const newStep = advancePrescriptionPadStep();
    // null is returned when there is no next step and script creation can proceed

    if (newStep === null) {
      await onCreateScript();
    }
  };

  return {
    primaryButtonTooltip,
    handleReviewFlow,
    isPrimaryButtonDisabled,
  };
};
